import moment from 'moment';
import { getItemFromLocalStorage, HUAWEI_SUCCESS_RETCODE } from '../../../api/auth';
import { getBaseUrl, getSessionTicket } from '../../../config';
import { HttpClient } from '../../../controllers/http-client/http-client';
import { Api, ExtensionInfoResponse } from '../../../interfaces';
import { devMiddleware } from '../../../middleware/dev-middleware';
import { store } from '../../../store';
import { convertRecordingDates } from '../../../utils/date-utils';
import { fromMoment } from '../../../utils/huaweiUtils';
import { setChannels } from '../../../views/tv/actions';
import { getChannels } from '../../channels/api';
import { queryClient } from '../../client';
import { bookmarksQuery } from '../me/bookmarks/queries';
import { createAsset, createSeriesAssets, FetchAssetOptions, getAssetDependencies } from '../titles/api';
import { titleQuery } from '../titles/queries';
import { Asset } from '../titles/types/asset';
import { ProgramSource } from '../titles/types/sources';
import { getSeriesRecordingEpisodes } from '../titles/utils/recording-utils';
import { createSources } from '../titles/utils/sources-utils';
import { RecordingError } from './errors';
import { HuaweiAddPvrResponse, HuaweiPeriodPvrMgmtResponse, HuaweiRecording } from './types/huawei-recording';
import { LinkedEpisodeRecording, Recording, SeriesRecording } from './types/recording';
import { findRecordingSeries, getSingleRecordingBy } from './utils/get-recording-by';
import { createRecordings } from './utils/recording-adapter';
import { createSeriesRecordingTitle, createSingleRecordingTitle } from './utils/title-adapter';

export class RecordingsHttpClient extends HttpClient {
  constructor() {
    super('');
    this.use((next) => {
      return async (request) => {
        const path = new URL(request.url).pathname;
        const url = new URL(path, getBaseUrl());

        const body = request.method === 'GET' ? undefined : await request.blob();
        request.headers.set('SessionTicket', getSessionTicket());
        const req = new Request(url.toString(), {
          ...request,
          method: request.method,
          headers: request.headers,
          body: body,
        });

        return next(req);
      };
    });
    this.use(devMiddleware);
  }
}

const client = new RecordingsHttpClient();

const RECORDING_STATUS = {
  SUCCESS: '0',
  ONGOING: '1',
  SCHEUDLED: '-1',
} as const;

const RECORDING_ORDER = {
  START_TIME_ASC: 0,
  START_TIME_DESC: 1,
  TASK_NAME_ASC: 2,
  TASK_NAME_DESC: 3,
  GENRE_NAME_ASC: 4,
  GENRE_NAME_DESC: 5,
  RECORDING_DURATION_ASC: 6,
  RECORDING_DURATION_DESC: 7,
  CHANNEL_NUMBER_ASC: 8,
  CHANNEL_NUMBER_DESC: 9,
  END_TIME_ASC: 10,
  END_TIME_DESC: 11,
};

const RECORDING_DELETE_MODE = {
  AUTO: 0,
  MANUAL: 1,
} as const;

export type DeleteMode = Lowercase<keyof typeof RECORDING_DELETE_MODE>;

export async function getRecordings(): Promise<Recording[]> {
  const response = await client.post<Api.PvrResponse<HuaweiRecording[]>>('/EPG/JSON/QueryPVR', {
    status: Object.values(RECORDING_STATUS).join(','),
    count: -1,
    offset: 0,
    pvrType: 2,
    isFilter: 0,
    expandSubTask: 2,
    orderType: RECORDING_ORDER.START_TIME_DESC,
    isReturnSeriesTask: 0,
  });

  const bookmarks = await queryClient.fetchQuery(bookmarksQuery());
  return createRecordings(response.pvrlist.map(convertRecordingDates), bookmarks);
}

export async function recordProgram(program: ProgramSource, deleteMode: DeleteMode) {
  const offsets = await recordingOffsets();
  const response = await client.post<HuaweiAddPvrResponse>('/EPG/JSON/AddPVR', {
    type: 2,
    channelNo: program.channelNumber,
    beginOffset: offsets.begin,
    endOffset: offsets.end,
    programId: program.platformTitleId,
    beginTime: fromMoment(moment(program.startTime)),
    endTime: fromMoment(moment(program.endTime)),
    deleteMode: RECORDING_DELETE_MODE[deleteMode.toUpperCase()],
  });

  return handleHuaweiResponse(response);
}

interface RecordSeriesOptions {
  deleteMode: DeleteMode;
  rerunsAllowed: boolean;
  seasonNumber: number;
  episodeNumber: number;
}

export async function recordSeries(program: ProgramSource, options: RecordSeriesOptions) {
  const mediaId = await getMediaId(program.channelId);

  if (!mediaId) {
    throw new Error('Media id not found');
  }

  const offsets = await recordingOffsets();
  const startTime = moment(program.startTime).subtract(offsets.begin, 'minutes');

  const { deleteMode, rerunsAllowed, seasonNumber, episodeNumber } = options;

  const response = await client.post<HuaweiPeriodPvrMgmtResponse>('/EPG/JSON/PeriodPVRMgmt', {
    action: 'ADD',
    programSerialEnable: 1,
    strategyType: 0,
    task: {
      type: 2,
      seriesType: 2,
      channelId: program.channelId,
      mediaId: mediaId,
      seriesID: program.seriesId,
      periodPVRTaskName: program.name,
      effectivetime: fromMoment(startTime),
      timeMode: rerunsAllowed ? 0 : 1,
      deleteMode: deleteMode === 'auto' ? 0 : 1,
      selectedSeasonNum: rerunsAllowed ? undefined : seasonNumber,
      selectedSubNum: rerunsAllowed ? undefined : episodeNumber,
    },
  });

  return handleHuaweiResponse(response);
}

export async function rescheduleSeries(task: SeriesRecording | LinkedEpisodeRecording, options: RecordSeriesOptions) {
  const offsets = await recordingOffsets();
  const startTime = moment(task.startTime).subtract(offsets.begin, 'minutes');

  const { deleteMode, rerunsAllowed, seasonNumber, episodeNumber } = options;

  const response = await client.post<HuaweiPeriodPvrMgmtResponse>('/EPG/JSON/PeriodPVRMgmt', {
    action: 'UPDATE',
    strategyType: 0,
    task: {
      type: 2,
      seriesType: 2,
      periodPVRTaskId: task.periodicTaskId,
      overtime: '-1',
      effectivetime: fromMoment(startTime),
      timeMode: rerunsAllowed ? 0 : 1,
      deleteMode: deleteMode === 'auto' ? 0 : 1,
      selectedSeasonNum: rerunsAllowed ? undefined : seasonNumber,
      selectedSubNum: rerunsAllowed ? undefined : episodeNumber,
    },
  });

  return handleHuaweiResponse(response);
}

export async function recordingOffsets() {
  const response = await client.post<ExtensionInfoResponse>('/EPG/JSON/QuerySubscriberEx', {
    profileId: getItemFromLocalStorage('user'),
    userType: 1,
  });

  const offsets = {
    begin: 3,
    end: 2,
  };

  if (response.retcode === HUAWEI_SUCCESS_RETCODE) {
    if (response.extensionInfo) {
      let leadTime = response.extensionInfo.find((x) => x.key === 'PVRDefaultLeadTime');
      offsets.begin = leadTime ? parseInt(leadTime.value, 10) : 2;

      let lagTime = response.extensionInfo.find((x) => x.key === 'PVRDefaultLagTime');
      offsets.end = lagTime ? parseInt(lagTime.value, 10) : 3;
    }
  }

  return offsets;
}

function handleHuaweiResponse(response: { retcode: string; retmsg: string }) {
  if (response.retcode !== '0') {
    throw new RecordingError(response.retcode);
  }

  return response;
}

async function getMediaId(channelId: string) {
  let channels = store.getState().channelsReducer.channels;

  if (channels.length === 0) {
    channels = await getChannels();
    store.dispatch(setChannels(channels));
  }

  const channel = channels.find((c) => c.contentId === channelId);
  return channel?.mediaId;
}

export async function setDeleteMode(recording: Recording, deleteMode: DeleteMode) {
  const mode = RECORDING_DELETE_MODE[deleteMode.toUpperCase()];

  if (recording.type === 'series') {
    return client.post('/EPG/JSON/PeriodPVRMgmt', {
      action: 'UPDATE',
      task: {
        periodPVRTaskId: recording.periodicTaskId,
        type: 2,
        deleteMode: mode,
      },
    });
  }

  return client.post('/EPG/JSON/UpdatePVR', {
    pvrId: recording.id,
    type: 2,
    deleteMode: mode,
  });
}

export async function toggleDeleteMode(recording: Recording) {
  const newMode = RECORDING_DELETE_MODE[recording.deleteMode === 'auto' ? 'MANUAL' : 'AUTO'];

  if (recording.type === 'series') {
    return client.post('/EPG/JSON/PeriodPVRMgmt', {
      action: 'UPDATE',
      task: {
        periodPVRTaskId: recording.periodicTaskId,
        type: 2,
        deleteMode: newMode,
      },
    });
  }

  return client.post('/EPG/JSON/UpdatePVR', {
    pvrId: recording.id,
    type: 2,
    deleteMode: newMode,
  });
}

export async function cancelProgramRecording(recordingId: string) {
  await client.post('/EPG/JSON/DeletePVR', {
    pvrId: recordingId,
    type: 2,
  });
}

export async function cancelSeriesRecording(recordingTaskId: string) {
  const now = moment();

  await client.post('/EPG/JSON/PeriodPVRMgmt', {
    action: 'UPDATE',
    strategyType: 0,
    task: {
      periodPVRTaskId: recordingTaskId,
      checkExist: 1,
      effectivetime: fromMoment(moment(now).subtract(1, 's')),
      overtime: fromMoment(now),
    },
  });
}

export async function deleteRecordings(recordingIds: string[]) {
  await client.post('/EPG/JSON/DeletePVR', {
    pvrId: recordingIds.join(','),
  });
}

export async function deleteRecordingTask(seriesOrTaskId: string) {
  await client.post('/EPG/JSON/DeleteSubPVR', {
    periodPVRId: seriesOrTaskId,
    status: '0,1,2,8',
  });
}

export interface GetRecordingAssetOptions extends FetchAssetOptions {
  type: 'single' | 'series';
}

export async function getRecordingAsset(id: string, options: GetRecordingAssetOptions): Promise<Asset> {
  try {
    const title = await queryClient.fetchQuery(titleQuery({ id, ...options }));

    if (title.type === 'episode' && options.type === 'series') {
      const series = await queryClient.fetchQuery(titleQuery({ id: title.series.masterId, ...options }));
      return createAsset(series);
    }

    return createAsset(title);
  } catch (error) {
    return createRecordingAsset(id, options);
  }
}

export async function createRecordingAsset(id: string, options: GetRecordingAssetOptions): Promise<Asset> {
  const [providers, recordings, products, bookmarks, genres] = await getAssetDependencies();

  const recording = getSingleRecordingBy(recordings, id, (rec) =>
    options.type === 'series' ? rec.series?.id || rec.programId : rec.programId,
  );

  if (recording === undefined) {
    throw new Error(`Recording not found with program id "${id}"`);
  }

  if (recording.type === 'linked' && options.type === 'series') {
    const series = findRecordingSeries(recording, recordings);

    const recordingWithMergedEpisodes: SeriesRecording = {
      ...series,
      episodes: getSeriesRecordingEpisodes(recordings, series.seriesId),
    };

    const title = createSeriesRecordingTitle(recordingWithMergedEpisodes, genres);

    return createSeriesAssets({
      title,
      dependencies: [providers, recordings, products, bookmarks, genres],
    });
  }

  const title = createSingleRecordingTitle(recording, genres);

  return {
    ...title,
    sources: createSources(title, providers, recordings, products, bookmarks),
  };
}
